import JsPDF from 'jspdf';
import { PAPER_SIZES } from 'src/config';
import { PaperSize, FrameOrientation } from 'src/types';

import {
  exportImagePosition,
  ImagePositionOptions,
} from './export-image-position';

export interface FrameObject {
  positionX: number;
  positionY: number;
  width: number;
  height: number;
}

/**
 * Calculates PDF dimensions based on paper size and orientation
 */
function getPdfDimensions(
  paperSize: PaperSize,
  orientation: FrameOrientation,
): { width: number; height: number } {
  const basePaperDimensions = PAPER_SIZES[paperSize];

  // When landscape, jsPDF swaps width/height automatically
  return {
    width:
      orientation === 'landscape'
        ? basePaperDimensions.height
        : basePaperDimensions.width,
    height:
      orientation === 'landscape'
        ? basePaperDimensions.width
        : basePaperDimensions.height,
  };
}

/**
 * Calculates image dimensions to fit paper while maintaining aspect ratio
 */
function calculateImageDimensions(
  frameObject: FrameObject,
  pdfWidth: number,
  pdfHeight: number,
): { width: number; height: number; x: number; y: number } {
  const imageAspectRatio = frameObject.width / frameObject.height;
  const paperAspectRatio = pdfWidth / pdfHeight;

  let imgWidth = pdfWidth;
  let imgHeight = pdfHeight;

  if (imageAspectRatio > paperAspectRatio) {
    // Image is wider than paper - fit to width
    imgHeight = pdfWidth / imageAspectRatio;
  } else {
    // Image is taller than paper - fit to height
    imgWidth = pdfHeight * imageAspectRatio;
  }

  // Center the image on the page
  const x = (pdfWidth - imgWidth) / 2;
  const y = (pdfHeight - imgHeight) / 2;

  return { width: imgWidth, height: imgHeight, x, y };
}

/**
 * Sanitizes a string for use in file names (removes path-unsafe characters).
 */
function sanitizeFileNamePart(value: string): string {
  return value.replace(/[/\\:*?"<>|]/g, '_').trim() || 'export';
}

/**
 * Exports a frame as PDF.
 * If fileName is provided it is used as the download name (without .pdf);
 * otherwise projectName is used.
 */
export async function exportPdfFrame({
  element,
  frameObject,
  pixelRatio = 4,
  projectName,
  paperSize,
  orientation,
  fileName,
}: {
  element: HTMLElement;
  frameObject: FrameObject;
  pixelRatio: number;
  projectName: string;
  paperSize: PaperSize;
  orientation: FrameOrientation;
  /** Optional base name for the file (without .pdf). e.g. projectName_revision_exportDate */
  fileName?: string;
}): Promise<void> {
  // Use higher pixel ratio for PDF to ensure sharp text
  // PDF needs much higher resolution than screen display
  const pdfPixelRatio = Math.max(pixelRatio * 2, 6);

  // Export the cropped image with high resolution
  const dataUrl = await exportImagePosition({
    element,
    imageOptions: frameObject as ImagePositionOptions,
    // max pixel ratio is 6
    pixelRatio: Math.min(pdfPixelRatio, 6),
  });

  const { width: pdfWidth, height: pdfHeight } = getPdfDimensions(
    paperSize,
    orientation,
  );

  // Create PDF document with specified orientation and high compression
  const pdf = new JsPDF({
    orientation,
    unit: 'mm',
    format: paperSize.toLowerCase(),
    compress: true,
    precision: 16,
  });

  // Calculate image dimensions to fit the paper while maintaining aspect ratio
  const {
    width: imgWidth,
    height: imgHeight,
    x,
    y,
  } = calculateImageDimensions(frameObject, pdfWidth, pdfHeight);

  // Add the image to PDF
  pdf.addImage(dataUrl, 'PNG', x, y, imgWidth, imgHeight);

  const baseName = fileName ?? sanitizeFileNamePart(projectName);
  pdf.save(`${baseName}.pdf`);
}

export async function exportPdfFrameAsBlob({
  element,
  frameObject,
  pixelRatio = 4,
  projectName,
  paperSize,
  orientation,
  fileName,
}: {
  element: HTMLElement;
  frameObject: FrameObject;
  pixelRatio: number;
  projectName: string;
  paperSize: PaperSize;
  orientation: FrameOrientation;
  fileName?: string;
}): Promise<{ blob: Blob; filename: string }> {
  const pdfPixelRatio = Math.max(pixelRatio * 2, 6);
  const dataUrl = await exportImagePosition({
    element,
    imageOptions: frameObject as ImagePositionOptions,
    pixelRatio: Math.min(pdfPixelRatio, 6),
  });

  const { width: pdfWidth, height: pdfHeight } = getPdfDimensions(
    paperSize,
    orientation,
  );
  const pdf = new JsPDF({
    orientation,
    unit: 'mm',
    format: paperSize.toLowerCase(),
    compress: true,
    precision: 16,
  });

  const {
    width: imgWidth,
    height: imgHeight,
    x,
    y,
  } = calculateImageDimensions(frameObject, pdfWidth, pdfHeight);
  pdf.addImage(dataUrl, 'PNG', x, y, imgWidth, imgHeight);

  const baseName = fileName ?? sanitizeFileNamePart(projectName);
  const blob = pdf.output('blob');
  return { blob, filename: `${baseName}.pdf` };
}
